import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface SubscriptionRow {
  readonly subscriptionId: string;
  readonly subscriptionCode: string;
  readonly saasTenantName: string | null;
  readonly saasTenantCode: string | null;
  readonly subscriptionType: string;
  readonly planCode: string | null;
  readonly status: string;
  readonly billingFrequency: string;
  readonly currency: string;
  readonly baseAmount: string;
}

const SUB_TYPES = ['STANDARD', 'CUSTOM', 'TRIAL', 'ENTERPRISE', 'PARTNER', 'GOVERNMENT'];
const SUB_STATUSES = ['DRAFT', 'PENDING', 'ACTIVE', 'GRACE', 'SUSPENDED', 'EXPIRED', 'CANCELLED', 'RENEWED'];
const FREQUENCIES = ['MONTHLY', 'QUARTERLY', 'ANNUAL', 'ONE_TIME', 'CONSUMPTION'];
const PAGE = 25;

/**
 * M44 §6 Subscription Dashboard (§28). Commercial operators browse the
 * subscription estate, register subscriptions and drive the lifecycle
 * (transition / renew / change-plan / cancel). Viewing needs
 * M44_SUBSCRIPTION_VIEW; create/cancel/transition need M44_SUBSCRIPTION_MANAGE;
 * renew needs M44_SUBSCRIPTION_RENEW; upgrade/downgrade needs
 * M44_SUBSCRIPTION_UPGRADE (SoD).
 */
export function SubscriptionDashboard(): ReactNode {
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [saasTenantId, setSaasTenantId] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('STANDARD');
  const [billingFrequency, setBillingFrequency] = useState('MONTHLY');
  const [baseAmount, setBaseAmount] = useState('0');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (typeFilter) params.set('subscriptionType', typeFilter);
    if (search.trim()) params.set('search', search.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: SubscriptionRow[]; total: number }>(`/v1/billing/subscriptions?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, typeFilter, search, page, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!saasTenantId.trim()) { setActionError('A billed customer (saasTenantId) is required.'); return; }
    try {
      await apiRequest('/v1/billing/subscriptions', {
        method: 'POST',
        body: { saasTenantId: saasTenantId.trim(), subscriptionType, billingFrequency, baseAmount: Number(baseAmount) || 0 },
        token,
      });
      setNotice('Subscription created.');
      setSaasTenantId('');
      setBaseAmount('0');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised to create subscriptions (M44_SUBSCRIPTION_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const act = async (id: string, verb: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/billing/subscriptions/${id}/${verb}`, { method: 'POST', body: {}, token });
      setNotice(`Subscription ${verb} succeeded.`);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(`You are not authorised for this action (${perm} required).`);
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="subscription-dashboard">
      <h1>Subscription Dashboard</h1>

      <section aria-label="subscription-create">
        <h2>Register Subscription</h2>
        <form aria-label="subscription-form" onSubmit={(e) => void create(e)}>
          <label>Billed Customer<input aria-label="field-saas-tenant" value={saasTenantId} onChange={(e) => setSaasTenantId(e.target.value)} /></label>
          <label>Type
            <select aria-label="field-subscription-type" value={subscriptionType} onChange={(e) => setSubscriptionType(e.target.value)}>
              {SUB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>Frequency
            <select aria-label="field-billing-frequency" value={billingFrequency} onChange={(e) => setBillingFrequency(e.target.value)}>
              {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label>Base Amount<input aria-label="field-base-amount" value={baseAmount} onChange={(e) => setBaseAmount(e.target.value)} /></label>
          <button type="submit" aria-label="submit-subscription">Create</button>
        </form>
        {actionError && <p role="alert" aria-label="subscription-action-error">{actionError}</p>}
        {notice && <p aria-label="subscription-notice">{notice}</p>}
      </section>

      <section aria-label="subscription-filters">
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {SUB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Type
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {SUB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>Search<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="subscription-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="subscription-total">{total} subscription(s).</p>
          <table aria-label="subscription-table">
            <thead>
              <tr><th scope="col">Code</th><th scope="col">Customer</th><th scope="col">Type</th><th scope="col">Plan</th><th scope="col">Frequency</th><th scope="col">Amount</th><th scope="col">Status</th><th scope="col">Actions</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8}>No subscriptions.</td></tr>
              ) : rows.map((s) => (
                <tr key={s.subscriptionId} data-subscription={s.subscriptionId}>
                  <td aria-label={`subscription-code-${s.subscriptionId}`}>{s.subscriptionCode}</td>
                  <td>{personLabel(s.saasTenantName, s.saasTenantCode) ?? '—'}</td>
                  <td>{s.subscriptionType}</td>
                  <td>{s.planCode ?? '—'}</td>
                  <td>{s.billingFrequency}</td>
                  <td>{s.currency} {money(Number(s.baseAmount))}</td>
                  <td aria-label={`subscription-status-${s.subscriptionId}`}>{s.status}</td>
                  <td>
                    <button type="button" aria-label={`renew-${s.subscriptionId}`} onClick={() => void act(s.subscriptionId, 'renew', 'M44_SUBSCRIPTION_RENEW')}>Renew</button>
                    <button type="button" aria-label={`change-plan-${s.subscriptionId}`} onClick={() => void act(s.subscriptionId, 'change-plan', 'M44_SUBSCRIPTION_UPGRADE')}>Change Plan</button>
                    <button type="button" aria-label={`cancel-${s.subscriptionId}`} onClick={() => void act(s.subscriptionId, 'cancel', 'M44_SUBSCRIPTION_MANAGE')}>Cancel</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="subscription-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
